import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Spinner, ChartLineUp, Users, Eye, ChatCircleDots, Buildings, DeviceMobile, Desktop } from "@phosphor-icons/react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const fmtDay = (d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" });

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid={testId}>
    <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-widest font-semibold"><Icon size={16} weight="bold" className="text-emerald-600" />{label}</div>
    <p className="font-display text-3xl font-extrabold mt-2 tabular-nums">{value}</p>
  </div>
);

const RankList = ({ title, rows, labelKey, testId }) => {
  const max = Math.max(1, ...rows.map((r) => r.views));
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid={testId}>
      <h3 className="font-display font-bold mb-3">{title}</h3>
      {rows.length === 0 ? <p className="text-sm text-zinc-400">No data yet.</p> : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <li key={r[labelKey]} className="text-sm">
              <div className="flex justify-between gap-3"><span className="truncate font-medium">{r[labelKey]}</span><span className="tabular-nums text-zinc-500 shrink-0">{r.views}{r.visitors != null ? ` · ${r.visitors} visitors` : ""}</span></div>
              <div className="h-1.5 rounded bg-zinc-100 mt-1"><div className="h-full rounded bg-emerald-500" style={{ width: `${(r.views / max) * 100}%` }} /></div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default function AdminTraffic() {
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/traffic?days=${days}`).then(({ data }) => { setData(data); setLoading(false); }).catch(() => setLoading(false));
  }, [days]);

  if (user.role !== "super_admin") return <div className="p-8 text-zinc-500" data-testid="traffic-denied">Super admin access only.</div>;

  const t = data?.totals || {};
  const dev = data?.devices || { mobile: 0, desktop: 0 };
  const devTotal = dev.mobile + dev.desktop || 1;

  return (
    <div className="space-y-4" data-testid="admin-traffic-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">Site Traffic</h1>
          <p className="text-zinc-500 text-sm mt-1">Public pages only · cookieless first-party tracking{GA_ID ? " · Google Analytics active" : ""}</p>
        </div>
        <div className="flex gap-1" data-testid="traffic-range">
          {[7, 30, 90].map((n) => (
            <Button key={n} size="sm" variant={days === n ? "default" : "outline"} className={days === n ? "bg-zinc-950 text-white" : "border-zinc-300"} onClick={() => setDays(n)} data-testid={`traffic-range-${n}`}>{n}d</Button>
          ))}
        </div>
      </div>

      {loading || !data ? <div className="py-20 text-center"><Spinner size={28} className="animate-spin text-emerald-600 mx-auto" /></div> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Stat icon={Users} label="Visitors" value={t.visitors} testId="traffic-visitors" />
            <Stat icon={Eye} label="Page views" value={t.views} testId="traffic-views" />
            <Stat icon={ChartLineUp} label="Today" value={t.today_visitors} testId="traffic-today" />
            <Stat icon={ChatCircleDots} label="Demo requests" value={t.demo_requests} testId="traffic-demos" />
            <Stat icon={Buildings} label="Signups" value={t.signups} testId="traffic-signups" />
          </div>

          <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="traffic-chart">
            <h3 className="font-display font-bold mb-3">Daily visitors</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.daily} margin={{ left: -20, right: 8, top: 4 }}>
                  <defs><linearGradient id="tv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity={0.35} /><stop offset="100%" stopColor="#10b981" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="day" tickFormatter={fmtDay} tick={{ fontSize: 11 }} minTickGap={24} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip labelFormatter={fmtDay} />
                  <Area type="monotone" dataKey="visitors" stroke="#10b981" strokeWidth={2} fill="url(#tv)" name="Visitors" />
                  <Area type="monotone" dataKey="views" stroke="#a1a1aa" strokeWidth={1.5} fill="transparent" name="Views" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-3">
            <RankList title="Top pages" rows={data.pages} labelKey="path" testId="traffic-pages" />
            <RankList title="Referrers" rows={data.referrers} labelKey="host" testId="traffic-referrers" />
            <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="traffic-devices">
              <h3 className="font-display font-bold mb-3">Devices</h3>
              <div className="flex h-3 rounded overflow-hidden bg-zinc-100"><div className="bg-emerald-500" style={{ width: `${(dev.mobile / devTotal) * 100}%` }} /><div className="bg-zinc-800" style={{ width: `${(dev.desktop / devTotal) * 100}%` }} /></div>
              <div className="mt-3 space-y-1 text-sm">
                <p className="flex items-center gap-2"><DeviceMobile size={16} className="text-emerald-600" />Mobile <span className="ml-auto tabular-nums">{dev.mobile} ({Math.round((dev.mobile / devTotal) * 100)}%)</span></p>
                <p className="flex items-center gap-2"><Desktop size={16} className="text-zinc-800" />Desktop <span className="ml-auto tabular-nums">{dev.desktop} ({Math.round((dev.desktop / devTotal) * 100)}%)</span></p>
              </div>
              {GA_ID ? (
                <a href="https://analytics.google.com/" target="_blank" rel="noreferrer" className="mt-4 inline-block text-sm font-semibold text-emerald-700 hover:underline" data-testid="traffic-ga-link">Open Google Analytics →</a>
              ) : (
                <p className="mt-4 text-xs text-zinc-400">Add a GA4 Measurement ID to unlock geography, traffic sources and more.</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
